import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-modal";

const modalStyles = {
  overlay: {
    backgroundColor: "rgba(15, 23, 42, 0.45)",
    zIndex: 2000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  content: {
    position: "relative",
    inset: "auto",
    width: "min(560px, 92vw)",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: 0,
    overflow: "hidden",
    background: "#fff",
    boxShadow: "0 25px 60px rgba(2, 6, 23, 0.25)",
  },
};

// Switch simple sin librerías
function Switch({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      aria-pressed={checked}
      className={`mc-switch ${checked ? "on" : "off"}`}
    >
      <span className="mc-switch-thumb" />
    </button>
  );
}

const ModalConfig = ({
  isOpen,
  onClose,

  // config inicial
  modoInicial,
  prefijoInicial,
  siguienteInicial,
  paddingInicial,

  // extras opcionales
  serieIdInicial,
  serieLabelInicial,
  reinicioInicial,
  seriesOptions,

  // callbacks
  onChangeNumeroFormatted,
  onManualNumeroChange,
}) => {
  const [serieId, setSerieId] = useState(serieIdInicial || "serie_a");
  const [serieLabel, setSerieLabel] = useState(serieLabelInicial || "");
  const [reinicio, setReinicio] = useState(reinicioInicial || "Anual");

  const [prefijo, setPrefijo] = useState(prefijoInicial || "");
  const [padding, setPadding] = useState(
    Number.isFinite(paddingInicial) && paddingInicial > 0 ? paddingInicial : 4,
  );

  const [manualEnabled, setManualEnabled] = useState(false);

  const [siguienteNumero, setSiguienteNumero] = useState(
    Number.isFinite(Number(siguienteInicial)) ? Number(siguienteInicial) : 1,
  );

  const [numeroManual, setNumeroManual] = useState("");

  // sync props -> state
  useEffect(() => setPrefijo(prefijoInicial || ""), [prefijoInicial]);

  useEffect(() => {
    setSiguienteNumero(
      Number.isFinite(Number(siguienteInicial)) ? Number(siguienteInicial) : 1,
    );
  }, [siguienteInicial]);

  useEffect(() => {
    setPadding(
      Number.isFinite(Number(paddingInicial)) && Number(paddingInicial) > 0
        ? Number(paddingInicial)
        : 4,
    );
  }, [paddingInicial]);

  useEffect(() => {
    if (serieIdInicial) setSerieId(serieIdInicial);
    if (serieLabelInicial) setSerieLabel(serieLabelInicial);
  }, [serieIdInicial, serieLabelInicial]);

  useEffect(() => {
    if (reinicioInicial) setReinicio(reinicioInicial);
  }, [reinicioInicial]);

  // Opciones de series
  const series = useMemo(() => {
    if (Array.isArray(seriesOptions) && seriesOptions.length > 0)
      return seriesOptions;
    const p = (prefijoInicial || prefijo || "COT").trim() || "COT";
    return [{ id: "serie_a", label: `Serie A – ${p} – Matriz` }];
  }, [seriesOptions, prefijoInicial, prefijo]);

  useEffect(() => {
    const found = series.find((s) => s.id === serieId);
    if (found && !serieLabel) setSerieLabel(found.label);
  }, [serieId, series, serieLabel]);

  const ultimoNumeroUsado = useMemo(() => {
    const n = Number(siguienteNumero || 1);
    return Math.max(0, n - 1);
  }, [siguienteNumero]);

  const preview = useMemo(() => {
    const p = (prefijo || "").trim();

    const rawAuto = String(siguienteNumero ?? 1).replace(/[^\d]/g, "") || "1";
    const nAuto = rawAuto.padStart(padding || 1, "0");

    if (manualEnabled) {
      const manualRaw = (numeroManual || "").trim();
      if (!manualRaw) return p ? `${p}-—` : "—";

      const digits = manualRaw.replace(/[^\d]/g, "");
      const formatted =
        digits.length > 0 ? digits.padStart(padding || 1, "0") : manualRaw;
      return p ? `${p}-${formatted}` : formatted;
    }

    return p ? `${p}-${nAuto}` : nAuto;
  }, [prefijo, siguienteNumero, padding, manualEnabled, numeroManual]);

  const guardarConfiguracionNumero = (e) => {
    e.preventDefault();

    const p = (prefijo || "").trim();

    if (manualEnabled) {
      const manualRaw = (numeroManual || "").trim();
      onManualNumeroChange?.(manualRaw);

      onChangeNumeroFormatted?.({
        modo: "manual",
        prefijo: p,
        siguiente: 1,
        padding: Number.isFinite(padding) && padding > 0 ? padding : 4,
        serieId,
        serieLabel:
          serieLabel || (series.find((s) => s.id === serieId)?.label ?? ""),
        reinicio,
        ultimoNumeroUsado,
        numeroManual: manualRaw,
      });
      return;
    }

    const raw = String(siguienteNumero ?? "").replace(/[^\d]/g, "");
    const width = raw.length || padding || 1;
    const siguiente = parseInt(raw, 10) || 1;

    onChangeNumeroFormatted?.({
      modo: "auto",
      prefijo: p,
      siguiente,
      padding: width,
      serieId,
      serieLabel:
        serieLabel || (series.find((s) => s.id === serieId)?.label ?? ""),
      reinicio,
      ultimoNumeroUsado: Math.max(0, siguiente - 1),
    });
  };

  return (
    <Modal
      isOpen={Boolean(isOpen)}
      onRequestClose={onClose}
      style={modalStyles}
      ariaHideApp={false}
    >
      <style>{`
        .mc-wrap{
          font-family: Roboto, system-ui, -apple-system, Segoe UI, Arial, sans-serif;
          background:#fff;
        }

        .mc-header{
          display:flex;
          align-items:center;
          justify-content:space-between;
          padding:18px 20px;
          border-bottom:1px solid #e5e7eb;
        }
        .mc-title{
          font-size:22px;
          font-weight:500;
          margin:0;
          color:#0f172a;
        }
        .mc-xbtn{
          padding:8px;
          border:none;
          background:transparent;
          border-radius:10px;
          cursor:pointer;
          transition: background-color .15s ease;
        }
        .mc-xbtn:hover{ background:#f3f4f6; }
        .mc-xicon{ width:22px; height:22px; color:#64748b; }

        .mc-body{
          padding:18px 20px 10px;
          display:flex;
          flex-direction:column;
          gap:14px;
        }

        /* label “Serie”, “Número” */
        .mc-label{
          display:flex;
          align-items:center;
          gap:8px;
          font-size:16px;
          font-weight:400;
          color:#0f172a;
          margin-bottom:10px;
        }
        .mc-required{ color:#ef4444; font-weight:600; }

        /* ==== ESTILO FIEL A TU INSPECCIÓN (gray-50 / gray-200 / rounded-lg / text-sm) ==== */
        .mc-field{
          width:100%;
          padding:10px 12px;
          background:#f9fafb;
          border:1px solid #e5e7eb;
          border-radius:8px;
          font-size:14px;
          font-weight:400;
          color:#111827;
          outline:none;
          box-sizing:border-box;
          transition: box-shadow .15s ease, border-color .15s ease;
        }
        .mc-field::placeholder{ color:#9ca3af; }
        .mc-field:focus{
          border-color: transparent;
          box-shadow: 0 0 0 2px var(--color-primary, rgba(59,130,246,.6));
        }
        .mc-field:disabled{
          opacity:.6;
          cursor:not-allowed;
        }

        .mc-summary{
          display:grid;
          grid-template-columns: 1fr 1fr;
          gap:16px;
          padding:10px 12px;
          background:#f9fafb;
          border:1px solid #e5e7eb;
          border-radius:8px;
        }
        .mc-summary-label{
          font-size:12px;
          color:#6b7280;
          margin:0 0 2px 0;
          font-weight:400;
        }
        .mc-summary-val{
          font-size:14px;
          font-weight:500;
          color:#111827;
          margin:0;
        }

        .mc-toggle-row{
          display:flex;
          align-items:center;
          gap:12px;
          margin-top:12px;
        }
        .mc-toggle-text{
          font-size:16px;
          font-weight:400;
          color:#0f172a;
        }

        .mc-switch{
          width:52px;
          height:30px;
          border-radius:999px;
          border:none;
          cursor:pointer;
          display:flex;
          align-items:center;
          padding:3px;
          transition: background-color .15s ease;
          background:#e5e7eb;
        }
        .mc-switch.on{ background:#3b82f6; }
        .mc-switch-thumb{
          width:24px;
          height:24px;
          border-radius:999px;
          background:#fff;
          transform: translateX(0);
          transition: transform .15s ease;
          box-shadow: 0 2px 8px rgba(15,23,42,.25);
        }
        .mc-switch.on .mc-switch-thumb{ transform: translateX(22px); }

        /* ✅ AVISO AMARILLO (Figma amber) */
        .mc-warn{
          display:flex;
          align-items:flex-start;
          gap:8px;
          padding:12px;                 /* p-3 */
          background:#fffbeb;           /* amber-50 */
          border:1px solid #fde68a;     /* amber-200 */
          border-radius:8px;            /* rounded-lg */
          margin-top:10px;
        }
        .mc-warn-icon{
          width:16px;                   /* w-4 */
          height:16px;                  /* h-4 */
          color:#d97706;                /* amber-600 */
          margin-top:2px;               /* mt-0.5 aprox */
          flex-shrink:0;
        }
        .mc-warn-text{
          font-size:12px;               /* text-xs */
          color:#92400e;                /* amber-800 */
          margin:0;
          line-height:1.35;
          font-weight:400;
        }

        .mc-preview{
          padding:16px;
          background:#f9fafb;
          border:1px solid #e5e7eb;
          border-radius:8px;
        }
        .mc-preview-label{
          font-size:12px;
          color:#6b7280;
          margin-bottom:4px;
          font-weight:400;
        }
        .mc-preview-val{
          font-size:18px;
          font-weight:500;
          color:#111827;
          font-family: monospace;
        }

        .mc-footer{
          display:flex;
          align-items:center;
          justify-content:flex-end;
          gap:14px;
          padding:16px 20px 18px;
          border-top:1px solid #e5e7eb;
        }
        .mc-cancel{
          border:none;
          background:transparent;
          padding:10px 14px;
          font-size:16px;
          font-weight:400;
          color:#0f172a;
          border-radius:12px;
          cursor:pointer;
        }
        .mc-cancel:hover{ background:#f3f4f6; }

        .mc-save{
          border:none;
          padding:12px 18px;
          font-size:16px;
          font-weight:500;
          color:#fff;
          border-radius:12px;
          cursor:pointer;
          background:#3b82f6;
        }
        .mc-save:hover{ opacity:.92; }
      `}</style>

      <div className="mc-wrap">
        <div className="mc-header">
          <h2 className="mc-title">Ajustar numeración</h2>

          <button
            type="button"
            className="mc-xbtn"
            onClick={onClose}
            aria-label="Cerrar"
            title="Cerrar"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mc-xicon"
              aria-hidden="true"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={guardarConfiguracionNumero}>
          <div className="mc-body">
            {/* Serie */}
            <div>
              <div className="mc-label">
                Serie <span className="mc-required">*</span>
              </div>

              <select
                className="mc-field"
                value={serieId}
                onChange={(e) => {
                  const id = e.target.value;
                  setSerieId(id);
                  const found = series.find((s) => s.id === id);
                  if (found) setSerieLabel(found.label);
                }}
              >
                {series.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Summary */}
            <div className="mc-summary">
              <div>
                <p className="mc-summary-label">Último número usado</p>
                <p className="mc-summary-val">{ultimoNumeroUsado}</p>
              </div>
              <div>
                <p className="mc-summary-label">Reinicio</p>
                <p className="mc-summary-val">{reinicio}</p>
              </div>
            </div>

            {/* Número */}
            <div>
              <div className="mc-label">
                Número <span className="mc-required">*</span>
              </div>

              <input
                className="mc-field"
                type="text"
                disabled={!manualEnabled}
                value={
                  manualEnabled
                    ? numeroManual
                    : String(siguienteNumero ?? "").replace(/[^\d]/g, "")
                }
                placeholder={manualEnabled ? "Ej. 126" : ""}
                onChange={(e) => {
                  const v = e.target.value;

                  if (manualEnabled) {
                    setNumeroManual(v);
                    onManualNumeroChange?.(v.trim());
                    return;
                  }

                  const cleaned = v.replace(/[^\d]/g, "");
                  setSiguienteNumero(parseInt(cleaned || "1", 10));
                }}
              />

              {/* toggle */}
              <div className="mc-toggle-row">
                <Switch
                  checked={manualEnabled}
                  onChange={(v) => {
                    setManualEnabled(v);
                    if (!v) setNumeroManual("");
                  }}
                />
                <div className="mc-toggle-text">Editar número manualmente</div>
              </div>

              {/* ✅ AVISO SOLO CUANDO ESTÁ ACTIVADO */}
              {manualEnabled && (
                <div className="mc-warn" role="note" aria-live="polite">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mc-warn-icon"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" x2="12" y1="8" y2="12" />
                    <line x1="12" x2="12.01" y1="16" y2="16" />
                  </svg>

                  <p className="mc-warn-text">
                    Modificar manualmente puede afectar la secuencia automática.
                  </p>
                </div>
              )}
            </div>

            {/* Vista previa */}
            <div className="mc-preview">
              <div className="mc-preview-label">Vista previa</div>
              <div className="mc-preview-val">{preview}</div>
            </div>
          </div>

          <div className="mc-footer">
            <button type="button" className="mc-cancel" onClick={onClose}>
              Cancelar
            </button>

            <button type="submit" className="mc-save">
              Guardar numeración
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ModalConfig;
